import classnames from 'classnames';
import type { FunctionComponent, PropsWithChildren } from 'react';

import styles from './scss/Option.module.scss';

/**
 * Option component.
 */

export interface OptionProps extends PropsWithChildren {
  /** A value linked to the option */
  value: string;
  /** To override the style for input */
  className?: string;
  /** To enable selection style */
  isSelected?: boolean;
  /** To enable disable wordwrap else appears in single line, hidden with ellipse */
  isWordWrap?: boolean;
  /** To enable/disable the component */
  isDisabled?: boolean;
  /** To set focus */
  isFocused?: boolean;
  /** To set selected class style */
  classSelected?: string;
  /** To set focused class style */
  classFocus?: string;
  /** To set disabled class style */
  classDisabled?: string;
}

export const Option: FunctionComponent<OptionProps> = ({
  className,
  value,
  isDisabled = false,
  isSelected = false,
  isFocused = false,
  isWordWrap = false,
  classSelected,
  classFocus,
  classDisabled,
  children,
  ...props
}) => {
  const currentWordWrap = isWordWrap ? styles.wordWrap : styles.noWordWrap;

  return (
    <li
      className={classnames([
        ...(!isDisabled
          ? [styles.option, className]
          : [styles.optionDisabled, classDisabled]),
        currentWordWrap,
        ...(isSelected && !isDisabled
          ? [styles.optionSelected, classSelected]
          : []),
        ...(isFocused && !isDisabled ? [styles.optionFocused, classFocus] : []),
      ])}
      aria-selected={isSelected}
      tabIndex={-1}
      role="option"
      value={value}
      {...props}
    >
      {children}
    </li>
  );
};

Option.displayName = 'Option';
